/**
 * Return a promise that is resolved or rejected depending on the response's
 * status code.
 */
export const _checkStatus = (res) => {
    if (res.status >= 400) {
        // Need to wrap the response in an object so that it matches the same error object
        // returned by _catchStatusError
        return Promise.reject(res);
    }

    return Promise.resolve(res);
};

export const _tryParseJSON = (res) => {
    try {
        return (
            res
                .json() // if JSON cannot parse, it'll return a rejected promise instead
                // of throwing an error, so we catch that rejection so that we can rejected
                // with the response like everything else expects
                .catch(function () {
                    return Promise.reject(res);
                })
        );
    } catch (error) {
        return Promise.reject(res);
    }
};

const _hasArgumentsError = (responseData) => {
    return !!(responseData['error_detail'] && responseData['error_detail']['ARGUMENTS_ERROR']);
};

/**
 * Parse v3 errors into an array of objects representing the errors returned by
 * the API. The format of the parsed errors looks like:
 *
 *  {
 *      status_code: 400,
 *      error: 'ERROR_CODE',
 *      description: 'Description of the error
 *  }
 *
 * An ARGUMENTS_ERROR looks like:
 *
 *  {
 *      error: 'ARGUMENTS_ERROR',
 *      description: 'Some of the fields were invalid or something',
 *      argumentErrors: {
 *          attr1: ['INVALID'],
 *          attr2: ['This field is required']
 *      }
 *  }
 *
 */
var _parseError = (responseData) => {
    if (!responseData.error) {
        // Weird error format, return null
        return null;
    }

    let error = {
        error: responseData.error,
        description: responseData['error_description'],
    };

    if (_hasArgumentsError(responseData)) {
        error = {
            ...error,
            ...{
                argumentErrors: responseData['error_detail']['ARGUMENTS_ERROR'],
            },
        };
    }

    return error;
};

/**
 * Designed to work with `_checkStatus`, or any function that
 * raises an error on an invalid status. The error raised should have a `response`
 * property with the original response object.
 *
 * Example usage:
 *
 * _fetchJSON('/api/v3/test/path', {'body': someData})
 *     .catch(_catchStatusError)
 *     .then(doSomethingOnSuccess)
 *     .catch(({response, parsedError}) => doSomethingOnError());
 */
export const _catchStatusError = (res) => {
    return new Promise(function (resolve, reject) {
        _tryParseJSON(res) // handled error, so reject with parsed error data along with response
            .then((responseData) => {
                return reject({
                    response: res,
                    parsedError: _parseError(responseData),
                });
            }) // Unhandled error
            .catch(() => {
                return reject({
                    response: res,
                });
            });
    });
};
